import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ClassroomHomeworkCompletionReport,
  HomeworkApi,
  HomeworkCompletionDetail,
  HomeworkCompletionReportItem,
  HomeworkReportClassroom,
} from '../../services/homeworkApi'

const BUCKET_LABEL: Record<string, string> = {
  notStarted: 'Chưa làm',
  inProgress: 'Đang làm',
  completed: 'Hoàn thành',
}

export default function AdminHomeworkReportPage() {
  const [classrooms, setClassrooms] = useState<HomeworkReportClassroom[]>([])
  const [classroomId, setClassroomId] = useState<number | ''>('')
  const [report, setReport] = useState<ClassroomHomeworkCompletionReport | null>(null)
  const [loadingList, setLoadingList] = useState(true)
  const [loadingReport, setLoadingReport] = useState(false)
  const [detail, setDetail] = useState<HomeworkCompletionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailBucket, setDetailBucket] = useState<'all' | 'notStarted' | 'inProgress' | 'completed'>(
    'all'
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoadingList(true)
        const list = await HomeworkApi.listReportClassrooms()
        if (!cancelled) setClassrooms(list)
      } catch (e: any) {
        if (!cancelled) toast.error(e?.message || 'Không tải được danh sách lớp')
      } finally {
        if (!cancelled) setLoadingList(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!classroomId) {
      setReport(null)
      setDetail(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setLoadingReport(true)
        setDetail(null)
        const data = await HomeworkApi.getClassroomCompletionReport(Number(classroomId))
        if (!cancelled) setReport(data)
      } catch (e: any) {
        if (!cancelled) {
          setReport(null)
          toast.error(e?.message || 'Không tải được báo cáo')
        }
      } finally {
        if (!cancelled) setLoadingReport(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [classroomId])

  const openDetail = async (item: HomeworkCompletionReportItem) => {
    if (!classroomId) return
    try {
      setDetailLoading(true)
      setDetailBucket('all')
      const data = await HomeworkApi.getHomeworkCompletionReport(item.homeworkId, Number(classroomId))
      setDetail(data)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được chi tiết')
    } finally {
      setDetailLoading(false)
    }
  }

  const filteredStudents = useMemo(() => {
    if (!detail) return []
    if (detailBucket === 'all') return detail.students
    return detail.students.filter((s) => s.bucket === detailBucket)
  }, [detail, detailBucket])

  return (
    <div className="p-4 lg:p-8 max-w-6xl mx-auto">
      <div className="mb-4">
        <Link to="/admin/homework-submissions" className="text-sm text-m2-primary hover:underline">
          ← Quay lại Bài làm học viên
        </Link>
        <h1 className="text-2xl font-bold mt-2">Báo cáo kết quả BTVN</h1>
        <p className="text-sm text-m2-on-surface-variant mt-1">
          Chọn lớp để xem tỉ lệ chưa làm / đang làm / hoàn thành theo từng BTVN.
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Lớp học</label>
        <select
          value={classroomId === '' ? '' : String(classroomId)}
          onChange={(e) => setClassroomId(e.target.value ? Number(e.target.value) : '')}
          disabled={loadingList}
          className="w-full max-w-xl border border-m2-outline-variant rounded-md px-3 py-2 text-sm"
        >
          <option value="">{loadingList ? 'Đang tải…' : '— Chọn lớp —'}</option>
          {classrooms.map((c) => (
            <option key={c.id} value={c.id}>
              {c.courseCode ? `${c.courseCode} · ` : ''}
              {c.name}
              {c.startDate ? ` (${new Date(c.startDate).toLocaleDateString('vi-VN')})` : ''}
            </option>
          ))}
        </select>
      </div>

      {!classroomId ? (
        <p className="text-m2-on-surface-variant text-sm">Chọn một lớp để xem báo cáo.</p>
      ) : loadingReport ? (
        <p className="text-m2-on-surface-variant text-sm">Đang tải báo cáo…</p>
      ) : !report ? (
        <p className="text-m2-on-surface-variant text-sm">Không có dữ liệu.</p>
      ) : (
        <>
          <div className="mb-4 text-sm text-m2-on-surface-variant">
            Lớp <span className="font-medium text-m2-on-surface">{report.classroomName}</span>
            {report.courseName ? ` · ${report.courseName}` : ''} ·{' '}
            <span className="font-medium text-m2-on-surface">{report.totalStudents}</span> học viên
          </div>

          {report.items.length === 0 ? (
            <p className="text-m2-on-surface-variant text-sm">Lớp này chưa được gán BTVN nào.</p>
          ) : (
            <>
              <div className="overflow-x-auto border rounded-xl mb-8">
                <table className="min-w-full text-sm">
                  <thead className="bg-m2-surface-high text-left">
                    <tr>
                      <th className="px-3 py-2">Tên BTVN</th>
                      <th className="px-3 py-2">Loại</th>
                      <th className="px-3 py-2 text-right">Đang làm</th>
                      <th className="px-3 py-2 text-right">Hoàn thành</th>
                      <th className="px-3 py-2 text-right">Chưa làm</th>
                      <th className="px-3 py-2 text-right">% HT</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item) => (
                      <tr key={item.homeworkId} className="border-t">
                        <td className="px-3 py-2 font-medium">{item.homeworkName}</td>
                        <td className="px-3 py-2">
                          {item.homeworkType === 'self_practice' ? 'Tự thực hành' : 'Nộp bài'}
                        </td>
                        <td className="px-3 py-2 text-right">{item.inProgress}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{item.completed}</td>
                        <td className="px-3 py-2 text-right text-amber-700">{item.notStarted}</td>
                        <td className="px-3 py-2 text-right">{item.completionRate}%</td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => void openDetail(item)}
                            className="text-m2-primary hover:underline text-sm"
                          >
                            Chi tiết
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="text-lg font-semibold mb-3">Biểu đồ theo từng BTVN</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {report.items.map((item) => {
                  const chartData = [
                    {
                      name: 'Trạng thái',
                      chuaLam: item.notStarted,
                      dangLam: item.inProgress,
                      hoanThanh: item.completed,
                    },
                  ]
                  return (
                    <div
                      key={item.homeworkId}
                      className="border border-m2-outline-variant rounded-xl p-4 bg-m2-surface-container"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="font-medium text-sm text-m2-on-surface line-clamp-2">
                            {item.homeworkName}
                          </p>
                          <p className="text-xs text-m2-on-surface-variant mt-0.5">
                            {item.homeworkType === 'self_practice' ? 'Tự thực hành' : 'Nộp bài'} ·{' '}
                            {item.completionRate}% HT
                          </p>
                        </div>
                      </div>
                      <div className="h-40">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis type="category" dataKey="name" width={0} tick={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="chuaLam" name="Chưa làm" stackId="a" fill="#d97706" />
                            <Bar dataKey="dangLam" name="Đang làm" stackId="a" fill="#0284c7" />
                            <Bar dataKey="hoanThanh" name="Hoàn thành" stackId="a" fill="#059669" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-m2-surface-container rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-m2-outline-variant">
              <div>
                <h3 className="text-lg font-bold text-m2-on-surface">
                  {detail?.homeworkName || 'Chi tiết'}
                </h3>
                {detail && (
                  <p className="text-xs text-m2-on-surface-variant mt-0.5">
                    Chưa làm {detail.notStarted} · Đang làm {detail.inProgress} · Hoàn thành{' '}
                    {detail.completed}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="text-sm px-3 py-1.5 rounded-lg hover:bg-m2-surface-high"
              >
                Đóng
              </button>
            </div>
            <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-m2-outline-variant">
              {(
                [
                  ['all', 'Tất cả'],
                  ['notStarted', 'Chưa làm'],
                  ['inProgress', 'Đang làm'],
                  ['completed', 'Hoàn thành'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDetailBucket(key)}
                  className={`px-3 py-1 rounded-full text-xs border ${
                    detailBucket === key
                      ? 'bg-m2-primary text-white border-m2-primary'
                      : 'border-m2-outline-variant text-m2-on-surface-variant'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {detailLoading ? (
                <p className="text-sm text-m2-on-surface-variant py-8 text-center">Đang tải…</p>
              ) : filteredStudents.length === 0 ? (
                <p className="text-sm text-m2-on-surface-variant py-8 text-center">Không có học viên</p>
              ) : (
                <ul className="divide-y divide-m2-outline-variant">
                  {filteredStudents.map((s) => (
                    <li key={s.userId} className="py-3 text-sm">
                      <div className="flex justify-between gap-3">
                        <div>
                          <p className="font-medium">{s.name || `#${s.userId}`}</p>
                          <p className="text-xs text-m2-on-surface-variant">{s.email}</p>
                          {s.completionNote ? (
                            <p className="text-xs mt-1 text-m2-on-surface-variant whitespace-pre-wrap">
                              Ghi chú: {s.completionNote}
                            </p>
                          ) : null}
                        </div>
                        <span className="text-xs shrink-0 font-medium">
                          {BUCKET_LABEL[s.bucket] || s.bucket}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
